import type {
  Frequency,
  RequirementTier,
  Source,
  VerificationStatus,
  WorklistRow,
} from '../api/verificationApi';

// ─── Numbers ─────────────────────────────────────────────────────────────────

const moneyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

export function formatMoney(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return moneyFormatter.format(value);
}

export function formatPercent(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return `${Math.round(value)}%`;
}

export function formatHours(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return `${Math.round(value)}h`;
}

export function formatYesNo(value: boolean | null | undefined): string {
  if (value == null) return '—';
  return value ? 'Yes' : 'No';
}

// ─── Dates ───────────────────────────────────────────────────────────────────

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Parses an ISO string; date-only values are treated as local dates (no TZ shift). */
function parseDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const m = DATE_ONLY.exec(iso);
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "Tue Sep 15 · 10:30 AM" */
export function formatDateTime(iso: string | null | undefined): string {
  const d = parseDate(iso);
  if (!d) return '—';
  const day = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${day} · ${time}`;
}

/** "Tue Sep 15, 10:30 AM" */
export function formatDateTimeInline(iso: string | null | undefined): string {
  const d = parseDate(iso);
  if (!d) return '—';
  const day = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${day}, ${time}`;
}

/** "Sep 14, 8:02 PM" — audit timestamps */
export function formatTimestamp(iso: string | null | undefined): string {
  const d = parseDate(iso);
  if (!d) return '—';
  const day = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${day}, ${time}`;
}

/** "Jan 1, 2026" */
export function formatShortDate(iso: string | null | undefined): string {
  const d = parseDate(iso);
  if (!d) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** MM/DD/YYYY */
export function formatDob(iso: string | null | undefined): string {
  const d = parseDate(iso);
  if (!d) return '—';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mm}/${dd}/${d.getFullYear()}`;
}

// ─── Frequency ───────────────────────────────────────────────────────────────

/** "2 / calendar year", "1 per 6 months", "1 / lifetime" */
export function formatFrequency(freq: Frequency | null | undefined): string | null {
  if (!freq) return null;
  switch (freq.per) {
    case 'benefit_year':
      return `${freq.quantity} / benefit year`;
    case 'calendar_year':
      return `${freq.quantity} / calendar year`;
    case 'lifetime':
      return `${freq.quantity} / lifetime`;
    case 'months':
      return freq.months ? `${freq.quantity} per ${freq.months} months` : `${freq.quantity} / period`;
    default:
      return `${freq.quantity}`;
  }
}

// ─── Status ──────────────────────────────────────────────────────────────────

export type PillKey =
  | 'pillSuccess'
  | 'pillWarning'
  | 'pillError'
  | 'pillInfo'
  | 'pillMuted'
  | 'pillPurple'
  | 'pillAccent';

export interface StatusDisplay {
  label: string;
  pill: PillKey;
}

const FAILURE_LABELS: Record<string, string> = {
  member_not_found: 'Member not found',
  missing_fields: 'Needs fix',
  no_payer_mapping: 'Needs payer',
};

export function statusDisplay(
  status: VerificationStatus,
  failureCode?: string | null,
): StatusDisplay {
  switch (status) {
    case 'complete':
      return { label: 'Verified', pill: 'pillSuccess' };
    case 'needs_review':
      return { label: 'Needs review', pill: 'pillWarning' };
    case 'in_progress':
      return { label: 'Checking…', pill: 'pillInfo' };
    case 'pending':
      return { label: 'Scheduled', pill: 'pillMuted' };
    case 'awaiting_production':
      return { label: 'Awaiting production', pill: 'pillPurple' };
    case 'failed':
      return {
        label: (failureCode && FAILURE_LABELS[failureCode]) || 'Failed',
        pill: 'pillError',
      };
    default:
      return { label: status, pill: 'pillMuted' };
  }
}

export function rowStatusDisplay(row: WorklistRow): StatusDisplay {
  return statusDisplay(row.status, row.failureCode);
}

export function isRunning(status: VerificationStatus): boolean {
  return status === 'pending' || status === 'in_progress';
}

export const STATUS_FILTERS: { value: VerificationStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'needs_review', label: 'Needs review' },
  { value: 'failed', label: 'Failed' },
  { value: 'complete', label: 'Verified' },
  { value: 'awaiting_production', label: 'Awaiting production' },
];

// ─── Sources ─────────────────────────────────────────────────────────────────

export interface ChipDisplay {
  label: string;
  pill: PillKey;
}

export function sourceDisplay(source: Source | string | null | undefined): ChipDisplay | null {
  switch (source) {
    case 'stedi_271':
      return { label: 'Electronic', pill: 'pillInfo' };
    case 'portal':
      return { label: 'Portal', pill: 'pillPurple' };
    case 'phone':
      return { label: 'Phone', pill: 'pillWarning' };
    case 'human':
      return { label: 'Reviewed', pill: 'pillSuccess' };
    case 'pms_existing':
      return { label: 'From PMS', pill: 'pillMuted' };
    default:
      return null;
  }
}

export function tierDisplay(tier: RequirementTier | string | null | undefined): ChipDisplay | null {
  switch (tier) {
    case 'electronic':
      return { label: 'Electronic', pill: 'pillInfo' };
    case 'portal':
      return { label: 'Portal', pill: 'pillPurple' };
    case 'phone':
      return { label: 'Phone', pill: 'pillWarning' };
    case 'human':
      return { label: 'Human', pill: 'pillSuccess' };
    default:
      return null;
  }
}

export function intakeLabel(intake: WorklistRow['intakeSource']): string {
  switch (intake) {
    case 'file_import':
      return 'Imported';
    case 'open_dental_sync':
      return 'Synced';
    default:
      return 'Manual';
  }
}

// ─── Confidence ──────────────────────────────────────────────────────────────

export type ConfidenceLevel = 'high' | 'medium' | 'low';

/** Confidence may arrive as 0–1 or 0–100; normalise to a 0–100 integer. */
export function confidencePct(confidence: number | null | undefined): number {
  if (confidence == null || Number.isNaN(confidence)) return 0;
  const pct = confidence <= 1 ? confidence * 100 : confidence;
  return Math.max(0, Math.min(100, Math.round(pct)));
}

export function confidenceLevel(confidence: number | null | undefined): ConfidenceLevel {
  const pct = confidencePct(confidence);
  if (pct >= 85) return 'high';
  if (pct >= 60) return 'medium';
  return 'low';
}

// ─── Misc labels ─────────────────────────────────────────────────────────────

export function coverageStatusLabel(value: string | null): string {
  switch (value) {
    case 'active':
      return 'Active';
    case 'inactive':
      return 'Inactive';
    case 'termed':
      return 'Termed';
    case 'unknown':
      return 'Unknown';
    default:
      return '—';
  }
}

export function networkStatusLabel(value: string | null): string {
  switch (value) {
    case 'in_network':
      return 'In-network';
    case 'out_of_network':
      return 'Out-of-network';
    case 'tiered':
      return 'Tiered';
    case 'unknown':
      return 'Unknown';
    default:
      return '—';
  }
}

export function relationshipLabel(value: string | null | undefined): string {
  switch (value) {
    case 'self':
      return 'self';
    case 'spouse':
      return 'spouse';
    case 'child':
      return 'child';
    case 'other':
      return 'other';
    default:
      return '—';
  }
}

export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

export function getErrorMessage(err: unknown, fallback = 'Something went wrong'): string {
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}
